import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { learningPathApi, courseApi, employeeApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { EmptyState, Spinner, Modal, ConfirmModal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon,
  TrashIcon,
  UserPlusIcon,
  MapIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';

function PathForm({ courses, initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState({
    name:         initial?.name         || '',
    description:  initial?.description  || '',
    target_roles: (initial?.target_roles || []).join(', '),
    target_bands: (initial?.target_bands || []).join(', '),
    course_ids:   initial?.items?.map(i => i.course_id) || [],
  });

  const toggle = (cid) => setForm(f => ({
    ...f,
    course_ids: f.course_ids.includes(cid) ? f.course_ids.filter(x => x !== cid) : [...f.course_ids, cid],
  }));

  const move = (cid, dir) => setForm(f => {
    const ids = [...f.course_ids];
    const idx = ids.indexOf(cid);
    const ni  = idx + dir;
    if (idx < 0 || ni < 0 || ni >= ids.length) return f;
    [ids[idx], ids[ni]] = [ids[ni], ids[idx]];
    return { ...f, course_ids: ids };
  });

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      name:         form.name,
      description:  form.description,
      target_roles: form.target_roles.split(',').map(s => s.trim()).filter(Boolean),
      target_bands: form.target_bands.split(',').map(s => s.trim()).filter(Boolean),
      course_ids:   form.course_ids,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="label">Path name</label>
        <input required className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
      </div>
      <div>
        <label className="label">Description</label>
        <textarea className="input h-20 resize-none" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Target roles (comma-separated)</label>
          <input className="input" value={form.target_roles} onChange={e => setForm({ ...form, target_roles: e.target.value })} placeholder="manager, employee" />
        </div>
        <div>
          <label className="label">Target bands</label>
          <input className="input" value={form.target_bands} onChange={e => setForm({ ...form, target_bands: e.target.value })} placeholder="X, E+" />
        </div>
      </div>

      <div>
        <label className="label">Courses — tap to add, reorder with arrows</label>
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-gray-200 rounded-lg p-2 max-h-64 overflow-y-auto space-y-1">
            <p className="text-xs text-gray-500 px-1 pb-1">Available</p>
            {courses.filter(c => !form.course_ids.includes(c.id)).map(c => (
              <button type="button" key={c.id} onClick={() => toggle(c.id)}
                className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50 flex items-center justify-between">
                <span className="truncate">{c.title}</span>
                <PlusIcon className="w-4 h-4 text-blue-600 shrink-0" />
              </button>
            ))}
            {courses.filter(c => !form.course_ids.includes(c.id)).length === 0 && (
              <p className="text-xs text-gray-400 px-1 py-4 text-center">All courses selected</p>
            )}
          </div>
          <div className="border border-gray-200 rounded-lg p-2 max-h-64 overflow-y-auto space-y-1 bg-blue-50/30">
            <p className="text-xs text-gray-500 px-1 pb-1">Selected (in order)</p>
            {form.course_ids.length === 0 && <p className="text-xs text-gray-400 px-1 py-4 text-center">No courses yet</p>}
            {form.course_ids.map((cid, idx) => {
              const c = courses.find(x => x.id === cid);
              if (!c) return null;
              return (
                <div key={cid} className="flex items-center gap-1 px-2 py-1.5 bg-white rounded border border-gray-100 text-sm">
                  <span className="text-xs text-gray-400 w-6">#{idx + 1}</span>
                  <span className="flex-1 truncate">{c.title}</span>
                  <button type="button" onClick={() => move(cid, -1)} className="p-0.5 hover:bg-gray-100 rounded"><ArrowUpIcon className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => move(cid,  1)} className="p-0.5 hover:bg-gray-100 rounded"><ArrowDownIcon className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => toggle(cid)}  className="p-0.5 hover:bg-red-50 text-red-500 rounded"><TrashIcon className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save path'}</button>
      </div>
    </form>
  );
}

function AssignModal({ path, employees, onAssign, onClose, assigning }) {
  const [empId, setEmpId] = useState('');
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">Assign <span className="font-semibold">{path?.name}</span> to an employee. They'll be auto-enrolled into every course in the path.</p>
      <div>
        <label className="label">Employee</label>
        <select className="input" value={empId} onChange={e => setEmpId(e.target.value)}>
          <option value="">Select an employee…</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.name} — {e.role}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!empId || assigning} onClick={() => onAssign(Number(empId))} className="btn-primary">
          {assigning ? 'Assigning…' : 'Assign'}
        </button>
      </div>
    </div>
  );
}

export default function LearningPaths() {
  const { user } = useAuth();
  const isHr     = ['hr_admin','super_admin'].includes(user?.role);
  const qc       = useQueryClient();

  const [formOpen, setFormOpen]       = useState(null); // { path? }
  const [assignOpen, setAssignOpen]   = useState(null); // path
  const [confirmDel, setConfirmDel]   = useState(null); // path

  const { data: paths = [], isLoading: loadingPaths } = useQuery({
    queryKey: ['learning-paths'],
    queryFn:  () => learningPathApi.list().then(r => r.data),
    enabled:  isHr,
  });

  const { data: myPaths = [], isLoading: loadingMine } = useQuery({
    queryKey: ['my-paths'],
    queryFn:  () => learningPathApi.mine().then(r => r.data),
  });

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn:  () => courseApi.list().then(r => r.data),
    enabled:  isHr,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-lite'],
    queryFn:  () => employeeApi.list({ per_page: 500 }).then(r => Array.isArray(r.data) ? r.data : (r.data.data || [])),
    enabled:  isHr,
  });

  const createMut = useMutation({
    mutationFn: learningPathApi.create,
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['learning-paths'] }); setFormOpen(null); toast.success('Path created'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to create path'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => learningPathApi.update(id, data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['learning-paths'] }); setFormOpen(null); toast.success('Path updated'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update path'),
  });
  const deleteMut = useMutation({
    mutationFn: learningPathApi.delete,
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['learning-paths'] }); toast.success('Path archived'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to archive'),
  });
  const assignMut = useMutation({
    mutationFn: ({ id, employee_id }) => learningPathApi.assign(id, { employee_id }),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['learning-paths'] });
      qc.invalidateQueries({ queryKey: ['my-paths'] });
      setAssignOpen(null);
      toast.success('Path assigned');
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to assign'),
  });

  if (loadingMine || (isHr && loadingPaths)) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="section-title">My assigned paths</h2>
        </div>
        {myPaths.length === 0 ? (
          <div className="card">
            <EmptyState
              title="No paths assigned to you"
              description="Your manager or HR will assign paths based on your role, band, and development goals."
            />
          </div>
        ) : (
          <div className="space-y-3">
            {myPaths.map(p => (
              <div key={p.assignment_id} className="card p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center shrink-0">
                      <MapIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">{p.name}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{p.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{p.progress_pct}%</p>
                    <p className="text-xs text-gray-500">{p.completed_at ? 'completed' : 'in progress'}</p>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mb-3">
                  <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${p.progress_pct}%` }} />
                </div>
                <div className="divide-y divide-gray-100">
                  {p.items?.map(it => (
                    <div key={it.item_id} className="flex items-center justify-between py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-6">#{it.order + 1}</span>
                        <span className="text-gray-800">{it.course?.title}</span>
                        {!it.is_required && <span className="text-xs text-gray-400">(optional)</span>}
                      </div>
                      <span className={clsx('text-xs px-2 py-0.5 rounded-full', {
                        'bg-green-100 text-green-800': it.status === 'completed',
                        'bg-amber-100 text-amber-800': it.status === 'in_progress',
                        'bg-gray-100 text-gray-700':   it.status === 'assigned' || it.status === 'not_enrolled',
                      })}>
                        {it.status.replace('_',' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {isHr && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title">All learning paths</h2>
            <button onClick={() => setFormOpen({})} className="btn-primary"><PlusIcon className="w-4 h-4" />New path</button>
          </div>

          {paths.length === 0 ? (
            <div className="card">
              <EmptyState
                title="No paths yet"
                description="Create a learning path by picking and ordering courses. Assign it to employees to auto-enrol them."
                action={<button onClick={() => setFormOpen({})} className="btn-primary"><PlusIcon className="w-4 h-4" />New path</button>}
              />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    {['Name','Courses','Target roles','Assigned','Actions'].map(h => <th key={h} className="th">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paths.map(p => (
                    <tr key={p.id} className="tr-hover">
                      <td className="td">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{p.name}</p>
                          <p className="text-xs text-gray-500 line-clamp-1">{p.description}</p>
                        </div>
                      </td>
                      <td className="td text-sm">{p.items?.length || 0}</td>
                      <td className="td text-xs text-gray-600">{(p.target_roles || []).join(', ') || '—'}</td>
                      <td className="td text-sm">{p.assignments_count ?? 0}</td>
                      <td className="td">
                        <div className="flex gap-2">
                          <button onClick={() => setAssignOpen(p)} className="btn-secondary btn-sm"><UserPlusIcon className="w-4 h-4" />Assign</button>
                          <button onClick={() => setFormOpen({ path: p })} className="btn-secondary btn-sm">Edit</button>
                          <button onClick={() => setConfirmDel(p)} className="btn-secondary btn-sm text-red-600"><TrashIcon className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <Modal open={!!formOpen} onClose={() => setFormOpen(null)} title={formOpen?.path ? 'Edit learning path' : 'New learning path'} size="lg">
        {formOpen && (
          <PathForm
            courses={courses}
            initial={formOpen.path}
            saving={createMut.isPending || updateMut.isPending}
            onCancel={() => setFormOpen(null)}
            onSubmit={(data) => {
              if (formOpen.path) updateMut.mutate({ id: formOpen.path.id, data });
              else createMut.mutate(data);
            }}
          />
        )}
      </Modal>

      <Modal open={!!assignOpen} onClose={() => setAssignOpen(null)} title={`Assign path`} size="sm">
        {assignOpen && (
          <AssignModal
            path={assignOpen}
            employees={employees}
            assigning={assignMut.isPending}
            onClose={() => setAssignOpen(null)}
            onAssign={(employee_id) => assignMut.mutate({ id: assignOpen.id, employee_id })}
          />
        )}
      </Modal>

      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={() => confirmDel && deleteMut.mutate(confirmDel.id)}
        title="Archive learning path?"
        message={`"${confirmDel?.name}" will be deactivated. Existing assignments remain.`}
        danger
      />
    </div>
  );
}
